// Simplified unified app store - Phase 3: State Management Optimization
import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { CoachingInsights, TrainingLoad, ChatMessage } from '@/types/business';

/**
 * Unified App Store Interface
 */
interface AppState {
  // Auth state
  user: User | null;
  session: Session | null;
  authLoading: boolean;
  authInitialized: boolean;
  authError?: string;

  // Dashboard state
  dashboardData: {
    recentRuns?: any[];
    recentFitness?: any[];
    bodyComposition?: any;
    photos?: any[];
    goals?: any[];
    achievements?: any[];
    lastUpdated?: number;
  };
  dashboardLoading: boolean;
  dashboardError: string | null;

  // Coaching state
  coachingData: {
    insights: {
      running: CoachingInsights | null;
      fitness: CoachingInsights | null;
    };
    chatHistory: ChatMessage[];
    preferences: {
      units: 'metric' | 'imperial';
      goals: string[];
      focusAreas: string[];
      aiPersonality: 'professional' | 'friendly' | 'motivational';
    };
    trainingLoad?: TrainingLoad;
  };

  // UI state
  theme: 'light' | 'dark' | 'system';
  sidebarCollapsed: boolean;
  notifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    timestamp: number;
  }>;

  // Cache state
  cache: Map<string, { data: any; timestamp: number; staleTime: number }>;
}

/**
 * App Store Actions
 */
interface AppActions {
  // Auth actions
  setAuth: (user: User | null, session: Session | null) => void;
  setAuthLoading: (loading: boolean) => void;
  setAuthError: (error?: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearAuthError: () => void;

  // Dashboard actions
  setDashboardData: (data: Partial<AppState['dashboardData']>) => void;
  setDashboardLoading: (loading: boolean) => void;
  setDashboardError: (error: string | null) => void;
  clearDashboardData: () => void;
  refreshDashboard: (userId: string) => Promise<void>;
  isDashboardStale: () => boolean;

  // Coaching actions
  setCoachingInsights: (type: 'running' | 'fitness', insights: CoachingInsights) => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatHistory: () => void;
  setCoachingPreferences: (preferences: Partial<AppState['coachingData']['preferences']>) => void;
  setTrainingLoad: (load: TrainingLoad) => void;
  clearCoachingInsights: () => void;

  // UI actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  toggleSidebar: () => void;
  addNotification: (notification: Omit<AppState['notifications'][0], 'id' | 'timestamp'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;

  // Cache actions
  cacheSet: <T>(key: string, data: T, staleTime?: number) => void;
  cacheGet: <T>(key: string) => T | null;
  cacheClear: () => void;
  cacheInvalidate: (keyPattern: string | RegExp) => void;

  // Meta actions
  resetStore: () => void;
  hydrateStore: () => Promise<void>;
}

type AppStore = AppState & AppActions;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Unified App Store
 */
export const useAppStore = create<AppStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Initial state
          user: null,
          session: null,
          authLoading: true,
          authInitialized: false,
          authError: undefined,

          dashboardData: {},
          dashboardLoading: false,
          dashboardError: null,

          coachingData: {
            insights: { running: null, fitness: null },
            chatHistory: [],
            preferences: {
              units: 'metric',
              goals: [],
              focusAreas: [],
              aiPersonality: 'friendly',
            },
            trainingLoad: undefined,
          },

          theme: 'system',
          sidebarCollapsed: false,
          notifications: [],

          cache: new Map(),

          // Auth actions
          setAuth: (user, session) => {
            set({ user, session, authError: undefined });
          },

          setAuthLoading: (loading) => {
            set({ authLoading: loading });
          },

          setAuthError: (error) => {
            set({ authError: error });
          },

          clearAuthError: () => {
            set({ authError: undefined });
          },

          signIn: async (email: string, password: string) => {
            try {
              set({ authLoading: true, authError: undefined });
              const { data, error } = await supabase.auth.signInWithPassword({ email, password });
              if (error) throw error;
              
              // Clear previous session data
              get().clearCoachingInsights();
              get().clearChatHistory();
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          signUp: async (email: string, password: string) => {
            try {
              set({ authLoading: true, authError: undefined });
              const { error } = await supabase.auth.signUp({ 
                email, 
                password,
                options: {
                  emailRedirectTo: `${window.location.origin}/`
                }
              });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          signOut: async () => {
            try {
              set({ authLoading: true, authError: undefined });
              const { error } = await supabase.auth.signOut();
              if (error) throw error;
              
              // Clear all user data
              set({ 
                user: null, 
                session: null,
                dashboardData: {},
                coachingData: {
                  insights: { running: null, fitness: null },
                  chatHistory: [],
                  preferences: {
                    units: 'metric',
                    goals: [],
                    focusAreas: [],
                    aiPersonality: 'friendly',
                  },
                  trainingLoad: undefined,
                }
              });
              get().cacheClear();
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign out failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          resetPassword: async (email: string) => {
            try {
              set({ authLoading: true, authError: undefined });
              const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/reset-password`
              });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          updatePassword: async (newPassword: string) => {
            try {
              set({ authLoading: true, authError: undefined });
              const { error } = await supabase.auth.updateUser({ password: newPassword });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Password update failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          updateEmail: async (newEmail: string) => {
            try {
              set({ authLoading: true, authError: undefined });
              const { error } = await supabase.auth.updateUser({ email: newEmail });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Email update failed';
              set({ authError: errorMessage });
              throw error;
            } finally {
              set({ authLoading: false });
            }
          },

          initializeAuth: async () => {
            try {
              if (get().authInitialized) return;

              set({ authLoading: true, authError: undefined });

              const { data: { session }, error } = await supabase.auth.getSession();
              if (error) {
                console.error('Error getting initial session:', error);
              }
              
              set({ 
                session, 
                user: session?.user ?? null, 
                authLoading: false,
                authInitialized: true 
              });

              // Set up auth state listener
              supabase.auth.onAuthStateChange((event, session) => {
                try {
                  set({ 
                    session, 
                    user: session?.user ?? null,
                    authLoading: false,
                    authError: undefined
                  });
                  
                  if (event === 'SIGNED_IN') {
                    window.location.href = '/dashboard';
                  } else if (event === 'SIGNED_OUT') {
                    window.location.href = '/auth';
                  }
                } catch (error) {
                  console.error('Error in auth state change listener:', error);
                  set({ authError: 'Authentication state change failed' });
                }
              });
            } catch (error) {
              console.error('Critical error during auth initialization:', error);
              const errorMessage = error instanceof Error ? error.message : 'Auth initialization failed';
              
              set({ 
                session: null, 
                user: null, 
                authLoading: false,
                authInitialized: true,
                authError: errorMessage
              });
              throw error;
            }
          },

          // Dashboard actions
          setDashboardData: (newData) => {
            set((state) => ({
              dashboardData: { 
                ...state.dashboardData, 
                ...newData, 
                lastUpdated: Date.now() 
              },
            }));
          },

          setDashboardLoading: (loading) => {
            set({ dashboardLoading: loading });
          },

          setDashboardError: (error) => {
            set({ dashboardError: error });
          },

          clearDashboardData: () => {
            set({ dashboardData: {}, dashboardError: null });
          },

          isDashboardStale: () => {
            const { dashboardData } = get();
            if (!dashboardData.lastUpdated) return true;
            return Date.now() - dashboardData.lastUpdated > CACHE_DURATION;
          },

          refreshDashboard: async (userId: string) => {
            try {
              set({ dashboardLoading: true, dashboardError: null });

              const { data, error } = await supabase.rpc('get_optimized_dashboard_summary', {
                p_user_id: userId,
                p_limit: 10
              });

              if (error) throw error;

              const dashboardData = {
                recentRuns: Array.isArray(data?.[0]?.recent_runs) ? data[0].recent_runs : [],
                recentFitness: Array.isArray(data?.[0]?.recent_fitness) ? data[0].recent_fitness : [],
                bodyComposition: data?.[0]?.latest_body_comp || null,
                photos: [],
                goals: [],
                achievements: Array.isArray(data?.[0]?.personal_records) ? data[0].personal_records : [],
                lastUpdated: Date.now()
              };

              set({ dashboardData, dashboardLoading: false });
              get().cacheSet(`dashboard-${userId}`, dashboardData, CACHE_DURATION);

            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
              set({ dashboardError: errorMessage, dashboardLoading: false });
              console.error('Dashboard refresh error:', error);
            }
          },

          // Coaching actions
          setCoachingInsights: (type, insights) => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                insights: {
                  ...state.coachingData.insights,
                  [type]: { ...insights, lastUpdated: Date.now() },
                },
              },
            }));
          },

          addChatMessage: (message) => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                chatHistory: [...state.coachingData.chatHistory, message].slice(-50), // Keep last 50
              },
            }));
          },

          clearChatHistory: () => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                chatHistory: [],
              },
            }));
          },

          setCoachingPreferences: (preferences) => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                preferences: {
                  ...state.coachingData.preferences,
                  ...preferences,
                },
              },
            }));
          },

          setTrainingLoad: (load) => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                trainingLoad: load,
              },
            }));
          },

          clearCoachingInsights: () => {
            set((state) => ({
              coachingData: {
                ...state.coachingData,
                insights: { running: null, fitness: null },
                trainingLoad: undefined,
              },
            }));
          },

          // UI actions
          setTheme: (theme) => {
            set({ theme });
            if (theme === 'system') {
              const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
              document.documentElement.classList.toggle('dark', prefersDark);
            } else {
              document.documentElement.classList.toggle('dark', theme === 'dark');
            }
          },

          toggleSidebar: () => {
            set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
          },

          addNotification: (notification) => {
            const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const newNotification = {
              ...notification,
              id,
              timestamp: Date.now(),
            };

            set((state) => ({
              notifications: [newNotification, ...state.notifications].slice(0, 10)
            }));

            if (notification.type !== 'error') {
              setTimeout(() => {
                get().removeNotification(id);
              }, 5000);
            }
          },

          removeNotification: (id) => {
            set((state) => ({
              notifications: state.notifications.filter(n => n.id !== id)
            }));
          },

          clearNotifications: () => {
            set({ notifications: [] });
          },

          // Cache actions
          cacheSet: <T>(key: string, data: T, staleTime = DEFAULT_STALE_TIME) => {
            set((state) => {
              const newCache = new Map(state.cache);
              newCache.set(key, {
                data,
                timestamp: Date.now(),
                staleTime,
              });
              return { cache: newCache };
            });
          },

          cacheGet: <T>(key: string): T | null => {
            const entry = get().cache.get(key);
            if (!entry) return null;
            
            const isStale = Date.now() - entry.timestamp > entry.staleTime;
            if (isStale) {
              get().cache.delete(key);
              return null;
            }
            
            return entry.data as T;
          },

          cacheClear: () => {
            set({ cache: new Map() });
          },

          cacheInvalidate: (keyPattern: string | RegExp) => {
            set((state) => {
              const newCache = new Map();
              for (const [key, entry] of state.cache) {
                const shouldKeep = typeof keyPattern === 'string' 
                  ? !key.includes(keyPattern)
                  : !keyPattern.test(key);
                if (shouldKeep) {
                  newCache.set(key, entry);
                }
              }
              return { cache: newCache };
            });
          },

          // Meta actions
          resetStore: () => {
            set({
              user: null,
              session: null,
              authLoading: false,
              authInitialized: false,
              authError: undefined,
              dashboardData: {},
              dashboardLoading: false,
              dashboardError: null,
              coachingData: {
                insights: { running: null, fitness: null },
                chatHistory: [],
                preferences: {
                  units: 'metric',
                  goals: [],
                  focusAreas: [],
                  aiPersonality: 'friendly',
                },
                trainingLoad: undefined,
              },
              notifications: [],
              cache: new Map(),
            });
          },

          hydrateStore: async () => {
            try {
              await get().initializeAuth();
              
              const user = get().user;
              if (user) {
                get().refreshDashboard(user.id);
                
                const cachedPreferences = get().cacheGet(`coaching-preferences-${user.id}`);
                if (cachedPreferences) {
                  get().setCoachingPreferences(cachedPreferences);
                }
              }
            } catch (error) {
              console.error('Store hydration error:', error);
              get().addNotification({
                type: 'error',
                title: 'Initialization Error',
                message: 'Failed to load application data'
              });
            }
          },
        }),
        {
          name: 'fitai-app-storage',
          partialize: (state) => ({
            user: state.user,
            session: state.session,
            theme: state.theme,
            sidebarCollapsed: state.sidebarCollapsed,
            coachingData: {
              preferences: state.coachingData.preferences,
            },
          }),
        }
      )
    ),
    { name: 'FitAI App Store' }
  )
);

// Legacy store selectors for backward compatibility
export const useAuthStore = () => {
  const {
    user, session, authLoading: loading, authInitialized: initialized, authError: error,
    setAuth, setAuthLoading: setLoading, signIn, signUp, signOut, resetPassword, 
    updatePassword, updateEmail, initializeAuth, clearAuthError
  } = useAppStore();
  
  return {
    user, session, loading, initialized, error,
    setAuth, setLoading, signIn, signUp, signOut, resetPassword, 
    updatePassword, updateEmail, clearAuthError,
    initialize: initializeAuth // Function reference, not value
  };
};

export const useDashboardStore = () => {
  const {
    dashboardData: data, dashboardLoading: loading, dashboardError: error,
    setDashboardData: setData, setDashboardLoading: setLoading, setDashboardError: setError,
    clearDashboardData: clearData, refreshDashboard: refreshData, isDashboardStale: isStale
  } = useAppStore();
  
  return {
    data, loading, error, setData, setLoading, setError, clearData, refreshData, isStale
  };
};

export const useCoachingStore = () => {
  const {
    coachingData,
    setCoachingInsights: setInsights, addChatMessage, clearChatHistory,
    setCoachingPreferences: setPreferences, setTrainingLoad, clearCoachingInsights: clearInsights
  } = useAppStore();
  
  return {
    insights: coachingData.insights,
    preferences: coachingData.preferences,
    chatHistory: coachingData.chatHistory,
    trainingLoad: coachingData.trainingLoad,
    setInsights, addChatMessage, clearChatHistory, setPreferences, setTrainingLoad, clearInsights,
    isInsightsStale: () => false // Simplified for now
  };
};

export const useCacheStore = () => {
  const { cacheSet, cacheGet, cacheClear, cacheInvalidate } = useAppStore();
  
  return {
    set: cacheSet,
    get: cacheGet, 
    clear: cacheClear,
    invalidate: cacheInvalidate,
    // Original cache interface signatures
    isStale: (key: string) => {
      const entry = useAppStore.getState().cache.get(key);
      if (!entry) return true;
      return Date.now() - entry.timestamp > entry.staleTime;
    },
    preload: async <T>(key: string, fetcher: () => Promise<T>, staleTime?: number) => {
      try {
        const cachedData = cacheGet<T>(key);
        const isStale = Date.now() - (useAppStore.getState().cache.get(key)?.timestamp || 0) > (staleTime || 5 * 60 * 1000);
        if (cachedData && !isStale) return;
        
        const data = await fetcher();
        cacheSet(key, data, staleTime);
      } catch (error) {
        console.warn(`Failed to preload cache for key: ${key}`, error);
      }
    },
    warmCache: async (entries: Array<{ key: string; fetcher: () => Promise<any>; staleTime?: number }>) => {
      const promises = entries.map(({ key, fetcher, staleTime }) =>
        useCacheStore().preload(key, fetcher, staleTime)
      );
      await Promise.allSettled(promises);
    },
    getStats: () => ({ 
      memoryEntries: useAppStore.getState().cache.size, 
      persistentEntries: 0, 
      totalSize: useAppStore.getState().cache.size, 
      hitRate: 0.8 
    }),
    cleanup: () => {
      // Clean up stale entries
      const cache = useAppStore.getState().cache;
      const now = Date.now();
      for (const [key, entry] of cache) {
        if (now - entry.timestamp > entry.staleTime) {
          cache.delete(key);
        }
      }
    }
  };
};